import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import ExpandableWidget from 'components/ExpandableWidget/ExpandableWidget'

import styles from './ProcessCostsWidget.module.sass'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'
import SegmentedControl from '@jimmyboys/liita-ui/dist/components/SegmentedControl'
import { useState } from 'react'
import { useFetchList } from 'store'
import { ProcessCosts } from 'models'

const getBudgetState = (costsPercentage) => {
	switch (true) {
		case costsPercentage >= 0 && costsPercentage <= 50:
			return 'healthy'
		case costsPercentage > 50 && costsPercentage < 80:
			return 'running-out'
		case costsPercentage >= 80:
			return 'unhealthy'
		default:
			return 'no-financial-state'
	}
}

const ProcessCostsWidget = (props) => {
	const { i18n, gotoRequisition } = useGlobal()
	const { data } = props
	const { process } = data
	const [selectedTab, setSelectedTab] = useState('sale')

	const {
		contents: [processCosts],
	} = useFetchList(
		[process?.id && `/clinical_processes/${process?.id}/costs`],
		[process?.id && ProcessCosts.fromJSON]
	)

	const tabs = [
		{
			id: 'sale',
			text: i18n`Sale`,
		},
		{
			id: 'purchase',
			text: i18n`Purchase`,
		},
	]

	const lastBudget = processCosts?.[0]?.lastBudgetDate?.shortDateFormat
	const reviewedAt = processCosts?.[0]?.reviewedAt?.shortDateFormat
	const budget = process?.plafond ?? 0

	const { surgicalCosts, ambulatoryCosts, surgicalCostsRequisitions } = (
		processCosts ?? []
	).reduce(
		(acc, elem) => {
			const amount =
				selectedTab === 'sale' ? elem.saleAmount : elem.purchaseAmount

			if (elem.surgeryRequisitionId) {
				acc['surgicalCosts'] += elem.quantity * amount

				if (amount) {
					acc['surgicalCostsRequisitions'].push(
						elem.surgeryRequisitionId
					)
				}

				return acc
			}

			acc['ambulatoryCosts'] += elem.quantity * amount

			return acc
		},
		{
			surgicalCosts: 0,
			ambulatoryCosts: 0,
			surgicalCostsRequisitions: [],
		}
	)

	const totalCosts = surgicalCosts + ambulatoryCosts
	const costsPercentage = (totalCosts * 100) / budget
	const budgetState = getBudgetState(costsPercentage)

	const renderTooltipRequisitionsList = (requisitionsList = []) => {
		return (
			<VStack>
				{requisitionsList.map((reqId) => {
					return (
						<small
							key={reqId}
							className={styles['link']}
							onClick={() => gotoRequisition(process?.id, reqId)}
						>{`#${reqId}`}</small>
					)
				})}
			</VStack>
		)
	}

	return (
		<ExpandableWidget
			className={styles['aside-widget']}
			titleAppearance='aside-widget'
			arrowPosition='right'
			heading='h5'
			icon='user'
			title={i18n`Process Costs`}
			expanded={true}
			expandable={true}
			borderRadius
			margins={['small', '', '']}
			paddings={['xsmall', 'xsmall', 'xsmall']}
		>
			<VStack gap='xsmall' className={styles['root']}>
				<SegmentedControl
					className={styles['segmented-control']}
					selectedTab={selectedTab}
					onSelect={(tab) => {
						setSelectedTab(tab?.id)
					}}
					tabs={tabs}
				/>
				<VStack align='center'>
					<small>
						<strong>{i18n`Total cost`}</strong>
					</small>
					<HStack className={styles['total-costs-container']}>
						<h3
							className={styles[`--budget-state-${budgetState}`]}
						>{`${totalCosts.toFixed(2)}€`}</h3>
						<h3>/</h3>
						<h3>{`${budget.toFixed(2)}€`}</h3>
					</HStack>
					<small>
						{lastBudget
							? i18n`*last budget at ${lastBudget}`
							: i18n`*without last budget`}
					</small>
				</VStack>
				<HStack className={styles['costs-container']}>
					<VStack>
						<HStack gap='xsmall'>
							<small>{i18n`Ambulatory cost`}</small>
							<small>
								<strong>{ambulatoryCosts.toFixed(2)}€</strong>
							</small>
							<small>
								{reviewedAt
									? i18n`*reviewed at ${reviewedAt}`
									: i18n`*not reviewed`}
							</small>
						</HStack>
						<HStack gap='normal'>
							{surgicalCostsRequisitions.length ? (
								<Tooltip
									content={renderTooltipRequisitionsList(
										surgicalCostsRequisitions
									)}
								>
									<small>{i18n`Surgical cost`}</small>
								</Tooltip>
							) : (
								<small>{i18n`Surgical cost`}</small>
							)}
							<small>
								<strong>{surgicalCosts.toFixed(2)}€</strong>
							</small>
						</HStack>
					</VStack>
				</HStack>
			</VStack>
		</ExpandableWidget>
	)
}

export default ProcessCostsWidget
